import { constants } from '~/utils/EmbeddingFeed/UniversalEmbedding/constants';
import { addStyles, removeStyles } from '~/utils/EmbeddingFeed/UniversalEmbedding/inspectorStyles';

/**
 * Класс создает инспектор элементов - inspector,
 * который подсвечивает блоки кода в которые можно вставить ленту рекомендаций.
 * В случае работы класса на мобильном устройстве создается якорь - anchor,
 * который заменяет указатель
 *
 * isMobileMethodEmbedding
 *  true - активирован мобильный метод встраивания
 *  false - метод встраивания на компьютере
 *
 * isBlockRedirection
 *  true - запрещается редирект
 *
 * touchAnchor - координаты нажатия на экран на мобильном устройстве
 *
 * container - блок над которым находится указатель или якорь
 *
 * selecting - функция обратного вызова аргументом которой будет выбранный html-элемент
 */
export class UniversalEmbedding {
  inspector: HTMLDivElement = null;
  anchor: HTMLDivElement = null;
  isMobileMethodEmbedding: boolean = false;
  isBlockRedirection: boolean = true;
  touchAnchor: ITouchAnchor = {
    x: 0,
    y: 0,
  };
  container: HTMLElement = null;
  selecting: ISelecting = null;

  constructor(method: MethodEmbedding, isBlockRedirection: boolean = true) {
    this.isBlockRedirection = isBlockRedirection;
    this.isMobileMethodEmbedding = method !== 'desktop';
  }

  /**
   * Начало перемещения якоря
   * @param event
   */
  startAnchorMove = (event: TouchEvent) => {
    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * Окончание перемещения якоря
   * @param event
   */
  endAnchorMove = (event: TouchEvent) => {
    this.anchor.style.top = `${event.changedTouches[0].clientY - 30}px`;
    this.highlightContainer();
  }

  /**
   * Создает html-модель якоря для вставки ленты на мобильных устройствах
   */
  createAnchor = (): void => {
    this.anchor = document.createElement('div');
    this.anchor.draggable = true;
    this.anchor.id = constants.ID_ANCHOR_ELEMENT;
    if (!document.getElementById(constants.ID_ANCHOR_ELEMENT)) {
      this.anchor = document.body.appendChild(this.anchor);
    }
    this.containerPositionUpdate(
      document.elementFromPoint(
        document.body.clientWidth / 2,
        this.anchor.getBoundingClientRect().y + constants.ANCHOR_OFFSET
      ) as HTMLElement
    );
    this.anchor.addEventListener('touchstart', this.startAnchorMove);
    this.anchor.addEventListener('touchend', this.endAnchorMove);
  }

  /**
   * Создает html-модель указателя (синяя полоска) места вставки ленты
   * При встраивании на мобильном устройстве + якорь для замены указателя
   */
  createInspector = (): void => {
    this.inspector = document.createElement('div');
    this.inspector.id = constants.ID_INSPECTOR_ELEMENT;
    const inscription = document.createElement('div');
    inscription.innerText = 'Нажмите, чтобы вставить ленту сюда'.toUpperCase();
    this.inspector.appendChild(inscription);
    if (!document.getElementById(constants.ID_INSPECTOR_ELEMENT)) {
      this.inspector = document.body.appendChild(this.inspector);
    }
    if (this.isMobileMethodEmbedding) this.createAnchor();
  }

  /**
   * Удаляет инспектор и якорь
   */
  removeInspector = () => {
    if (this.inspector) this.inspector.remove();
    if (this.anchor) {
      this.anchor.removeEventListener('touchstart', this.startAnchorMove);
      this.anchor.removeEventListener('touchend', this.endAnchorMove);
      this.anchor.remove();
    }
  }

  /**
   * Получает список запрещенных для вставки тегов
   * @returns
   */
  getExclusionSelector = (): string => constants.EXCLUDE_TAGS.join(',');

  /**
   * Обновляет позицию активного блока
   * @param container
   * @returns
   */
  containerPositionUpdate = (container: HTMLElement) => {
    const excludedSelector = this.getExclusionSelector();
    if (excludedSelector) {
      const excludedElements = Array.prototype.slice.call(
        document.querySelectorAll(excludedSelector)
      );
      if (excludedElements.indexOf(container) >= 0) return;
    }
    const pos = container.getBoundingClientRect();
    const width = pos.width;
    if (width < constants.ALLOWED_BLOCK_SIZE_FOR_EMBEDDING) return;
    const height = pos.height;
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const scrollLeft = window.scrollX || document.documentElement.scrollLeft;
    const top = Math.max(0, pos.top + scrollTop);
    const left = Math.max(0, pos.left + scrollLeft);
    this.inspector.style.top = top + 'px';
    this.inspector.style.left = left + 'px';
    this.inspector.style.width = width + 'px';
    this.inspector.style.height = height + 'px';
  }

  /**
   * Обрабатывает прокрутку страницы или перемещение курсора
   * @param event
   * @returns
   */
  highlightContainer = (event: Event = null) => {
    if (event) this.container = event.target as HTMLElement;
    if (this.isMobileMethodEmbedding) {
      this.container = document.elementFromPoint(
        document.body.clientWidth / 2,
        this.anchor.getBoundingClientRect().y + constants.ANCHOR_OFFSET
      ) as HTMLElement;
    }
    this.containerPositionUpdate(this.container);
  }

  /**
   * Отправляет выбранный элемент через функцию в переменной selecting
   * @param element
   */
  sendElement = (element: Element) => {
    if (this.selecting) this.selecting(element);
  }

  /**
   * Контроль нажатия на инспектор
   * @param clientX
   * @param clientY
   * @returns
   */
  hasClickOnInspector = (clientX: number, clientY: number): boolean => {
    const inspectorLabelPosition =
      this.inspector.children[0].getBoundingClientRect();

    return inspectorLabelPosition.left < clientX &&
      clientX < inspectorLabelPosition.right &&
      inspectorLabelPosition.top < clientY &&
      clientY < inspectorLabelPosition.bottom;
  }

  /**
   * Определяет выбранный элемент в мобильных устройствах
   * @param event
   */
  getElementByTouch = (event: TouchEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const element = document.elementFromPoint(
      this.touchAnchor.x,
      this.touchAnchor.y
    );
    this.sendElement(element);
  }

  /**
   * Определяет выбранный элемент при использовании указателя
   * @param event
   */
  getElementByClick = (event: MouseEvent) => {
    if (this.hasClickOnInspector(event.clientX, event.clientY)) {
      event.preventDefault();
      event.stopPropagation();
      this.sendElement(event.target as Element);
    }
  }

  /**
   * Реакция на touchstart
   * @param event
   */
  handlingStartMobileTouch = (event: TouchEvent) => {
    event.stopPropagation();
    this.touchAnchor = {
      x: event.changedTouches[0].clientX,
      y: event.changedTouches[0].clientY,
    };
  }

  /**
   * Реакция на touchend
   * @param event
   */
  handlingEndMobileTouch = (event: TouchEvent) => {
    if (
      this.touchAnchor.x === event.changedTouches[0].clientX &&
      this.touchAnchor.y === event.changedTouches[0].clientY &&
      this.hasClickOnInspector(
        event.changedTouches[0].clientX,
        event.changedTouches[0].clientY
      )
    ) {
      this.getElementByTouch(event);
    }
  }

  /**
   * Создает слушателей
   */
  startEngine = () => {
    if (this.isMobileMethodEmbedding) {
      window.addEventListener('scroll', this.highlightContainer);
      window.addEventListener('touchstart', this.handlingStartMobileTouch);
      window.addEventListener('touchend', this.handlingEndMobileTouch);
    } else {
      window.addEventListener('click', this.getElementByClick);
      window.addEventListener('mouseover', this.highlightContainer);
    }
    if (this.isBlockRedirection) window.onbeforeunload = () => true;
  }

  /**
   * Удаляет слушателей
   */
  stopEngine = () => {
    if (this.isMobileMethodEmbedding) {
      window.removeEventListener('scroll', this.highlightContainer);
      window.removeEventListener('touchstart', this.handlingStartMobileTouch);
      window.removeEventListener('touchend', this.handlingEndMobileTouch);
    } else {
      window.removeEventListener('click', this.getElementByClick);
      window.removeEventListener('mouseover', this.highlightContainer);
    }
    if (this.isBlockRedirection) window.onbeforeunload = undefined;
  }

  /**
   * Стартует процесс встраивания
   */
  start = (selecting: ISelecting) => {
    addStyles();
    this.selecting = selecting;
    this.createInspector();
    this.startEngine();
  }

  /**
   * Останавливает процесс встраивания
   */
  stop = () => {
    this.removeInspector();
    this.stopEngine();
    removeStyles();
  }
}
